// `extension-automation-candidate-criteria` — the first half of the automation screen: which
// candidates the automation may consider.
//
// Three controls, and the third is the ATS-filter table the criteria builder already ports. The
// two checkboxes carry inline `style="margin-top: 8px"` upstream, which is reproduced rather
// than moved to the stylesheet: this is a re-platform, and the sheet those rules would join
// (`_extension-automation-criteria.scss`) is ported verbatim beside it.
import type { ReactElement } from 'react'
import type { AtsFiltersFieldConfiguration, AtsFiltersSingleselectValues } from '@woo-ext/api'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { WooCollapsible } from '../common/WooCollapsible.tsx'
import { AtsFiltersSection } from '../search/AtsFiltersSection.tsx'
import type { AtsFilterRow } from '../search/atsFilterData.ts'

export interface AutomationCandidateCriteriaProps {
  /** `candidateOwnerJobOwner`. */
  ownerIsJobOwner?: boolean
  /** `excludeCandidateWithCurrentJobSubmission` — the one flag that defaults ON. */
  excludeWithJobSubmission?: boolean
  atsLabel?: string
  filterRows?: readonly AtsFilterRow[]
  filterFields?: readonly AtsFiltersFieldConfiguration[]
  singleselectValues?: AtsFiltersSingleselectValues
  onOwnerIsJobOwnerChange?: (value: boolean) => void
  onExcludeWithJobSubmissionChange?: (value: boolean) => void
  onFilterChange?: (index: number, row: AtsFilterRow) => void
  onFilterRemove?: (index: number) => void
  onFilterAdd?: () => void
}

export function AutomationCandidateCriteria({
  ownerIsJobOwner = false,
  excludeWithJobSubmission = false,
  atsLabel,
  filterRows = [],
  filterFields = [],
  singleselectValues = {},
  onOwnerIsJobOwnerChange,
  onExcludeWithJobSubmissionChange,
  onFilterChange,
  onFilterRemove,
  onFilterAdd,
}: AutomationCandidateCriteriaProps): ReactElement {
  return (
    <div>
      {filterFields.length === 0 ? null : (
        <WooCollapsible
          id="ats-filters-criteria-id"
          className="ats-filters"
          title={`${atsLabel ?? ''} candidate filters`}
          startOpen
        >
          <div className="ats-filters-wrap">
            <AtsFiltersSection
              rows={filterRows}
              fields={filterFields}
              singleselectValues={singleselectValues}
              {...(onFilterChange === undefined ? {} : { onChange: onFilterChange })}
              {...(onFilterRemove === undefined ? {} : { onRemove: onFilterRemove })}
              {...(onFilterAdd === undefined ? {} : { onAdd: onFilterAdd })}
            />
          </div>
        </WooCollapsible>
      )}
      <div className="extension-automation-candidate-criteria-owner-container" style={{ marginTop: 8 }}>
        <WooCheckbox
          checked={ownerIsJobOwner}
          onChange={(next) => {
            onOwnerIsJobOwnerChange?.(next)
          }}
        >
          Candidate owner = job owner
        </WooCheckbox>
      </div>
      <div className="extension-automation-candidate-criteria-job-submission-container" style={{ marginTop: 8 }}>
        <WooCheckbox
          checked={excludeWithJobSubmission}
          onChange={(next) => {
            onExcludeWithJobSubmissionChange?.(next)
          }}
        >
          Exclude candidates with a job submission (180 days unless specified)
        </WooCheckbox>
      </div>
    </div>
  )
}
